
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

/** Formats an ISO date string ("2026-05-12") as "12/05/2026". Returns a fallback for null/invalid input. */
export function formatFrenchDate(date: string | null | undefined, fallback = "—"): string {
  if (!date) return fallback;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

/** Formats an ISO date string ("2026-12-11") as "11 décembre 2026". Returns a fallback for null/invalid input. */
export function formatFrenchDateLong(date: string | null | undefined, fallback = "—"): string {
  if (!date) return fallback;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

/** Formats a number as a French-locale euro amount, e.g. 1490 -> "1 490 €". */
export function formatEuro(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  return `${amount.toLocaleString("fr-FR")} €`;
}

/**
 * Turns a URL slug into a display label: "marketing-digital" -> "Marketing Digital".
 *
 * Used as the last-resort label for dynamic route segments (categories, news
 * types, formation slugs) whenever the real name isn't loaded yet.
 */
export function humanizeSlug(slug: string): string {
  return decodeSegment(slug)
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Decodes a percent-encoded URL segment, falling back to the raw value if malformed. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
